import React, { useState } from 'react';
import clsx from 'clsx';
import { isDesktop } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useHistory } from 'react-router-dom';

import { pushSiteNotification } from 'containers/App/actions';

import DropdownButton from 'components/Button/DropdownButton/DropdownButton';
import APDropdown from 'components/Dropdown/APDropdown';
import Drawer from 'components/Drawer';
import DeleteConfirmation from 'components/DeleteConfirmation';
import { MobileSm, DefaultSm } from 'components/MediaQueries';
import { DELETE_LEAD_STATUS } from 'containers/Leads/types';
import ArchiveModal from './Leads.ArchiveModal';

/**
 * @description: OptionsButton Component for Leads Table
 * @param {*}
 * @return {*}
 */
export const LeadsOptionsButton = ({ leadId, orgId, contact, active, lostReasons, lostStatus }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.global.userInfo);
  const { integrationsEnabled: isIntegrationsEnabled, personas } = userInfo || {};
  const { pushNotification } = bindActionCreators({ pushNotification: pushSiteNotification }, dispatch);
  const [opened, setOpened] = useState(false);
  const [target, setTarget] = useState(null);
  const [archiveOpened, setArchiveOpened] = useState(false);
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);

  const { integrations = [] } = userInfo;
  const canSendEmail = isIntegrationsEnabled &&
    (personas?.isOrgOwner || personas?.isOrgMember) &&
    integrations.length > 0 && integrations.some((integration) => integration.grantStatus === 'valid');

  const { contactEmail = '', contactNumber = '' } = contact || {};

  const history = useHistory();

  const dismiss = () => {
    setOpened(false);
    target?.classList.add('hover-expose');
  };

  const deleteLeadRecord = async (e) => {
    e.stopPropagation();
    await dispatch({
      type: DELETE_LEAD_STATUS,
      payload: {
        variables: {
          id: leadId,
        },
      },
    });
    setDeleteConfirmOpened(false);
  };

  const copy = (string) => {
    navigator.clipboard.writeText(string);
  };

  const renderOptions = () => {
    const copyEmail = (e) => {
      e.stopPropagation();
      copy(contactEmail);
      pushNotification({
        message: 'Email copied to clipboard.',
        time: 3000,
      });
      dismiss();
    };

    const copyPhone = (e) => {
      e.stopPropagation();
      copy(contactNumber);
      pushNotification({
        message: 'Phone number copied to clipboard.',
        time: 3000,
      });
      dismiss();
    };

    const archive = (e) => {
      e.stopPropagation();
      setArchiveOpened(true);
      dismiss();
    };

    const deleteItem = (e) => {
      e.stopPropagation();
      setDeleteConfirmOpened(true);
      dismiss();
    };

    const goToLeadEmailer = (params) => {
      const searchParams = new URLSearchParams(params);
      // eslint-disable-next-line max-len
      history.push(`/app/v2/leads-emailer/${orgId}/${leadId}/${searchParams.size ? `?${searchParams.toString()}` : ''}`);
    };

    return (
      <>
        {canSendEmail && (
          <button
            className={clsx(
              'button--unstyled',
              'button--menu',
            )}
            onClick={() => goToLeadEmailer({ returnUrl: window.location.pathname, arg: 'new' })}
          >
            Create New Email
          </button>
        )}
        {canSendEmail && (
          <button
            className={clsx(
              'button--unstyled',
              'button--menu',
            )}
            onClick={() => goToLeadEmailer()}
          >
            View All Emails
          </button>
        )}
        {contactEmail && (
          <button
            className={clsx(
              'button--unstyled',
              'button--menu',
            )}
            onClick={copyEmail}
          >
            Copy Email Address
          </button>
        )}
        {contactNumber && (
          <button
            className={clsx(
              'button--unstyled',
              'button--menu',
            )}
            onClick={copyPhone}
          >
            Copy Phone Number
          </button>
        )}
        <button
          className={clsx(
            'button--unstyled',
            'button--menu',
          )}
          onClick={archive}
        >
          Mark as Lost
        </button>
        <button
          className={clsx(
            'button--unstyled',
            'button--menu',
          )}
          onClick={deleteItem}
        >
          Delete
        </button>
      </>
    );
  };

  const trigger = (
    <DropdownButton
      className="button--unstyled button--options"
    >
      <span className="fa fa-ellipsis-v" />
    </DropdownButton>
  );

  return (
    <>
      <DefaultSm>
        <APDropdown
          isOpen={opened}
          show={(e) => {
            setOpened(true);
            setTarget(e.currentTarget.parentNode.parentNode);
            e.currentTarget.parentNode.parentNode.classList.remove('hover-expose');
          }}
          dismiss={() => {
            if (opened) {
              setOpened(false);
              setTarget(null);

              if (isDesktop) {
                target.classList.add('hover-expose');
              }
            }
          }}
          triggerButton={trigger}
        >
          {renderOptions()}
        </APDropdown>
      </DefaultSm>
      <MobileSm>
        <Drawer
          isOpen={opened}
          show={() => setOpened(true)}
          dismiss={() => dismiss()}
          position="bottom"
          triggerButton={trigger}
        >
          {renderOptions()}
        </Drawer>
      </MobileSm>
      <ArchiveModal
        leadId={leadId}
        lostReasons={lostReasons}
        lostStatus={lostStatus}
        active={active}
        archiveOpened={archiveOpened}
        setArchiveOpened={setArchiveOpened}
      />
      <div onClick={(e) => e.stopPropagation()}>
        <DeleteConfirmation
          show={deleteConfirmOpened}
          handleClose={(e) => {
            e?.stopPropagation();
            setDeleteConfirmOpened(false);
          }}
          handleConfirm={deleteLeadRecord}
          deleteText="Delete Item"
          size="xs"
        >
          <p>Deleting this lead will remove it forever. Are you sure?</p>
        </DeleteConfirmation>
      </div>
    </>
  );
};
